import React, { useEffect, useState } from "react";
import { getVidyardUUID, getVidyardCoverImageUrl, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
/**
 * Renders a link to a Vidyard video with an optional lightbox player.
 *
 * @param {string} vidyardVideoUrl - The URL of the Vidyard video.
 * @param {string} linkText - The text to display on the link.
 * @param {Object} linkProps - Additional props to pass to the link element.
 * @param {Object} anchorProps - Additional props to pass to the anchor element.
 * @param {ReactNode} children - The child elements to render within the link.
 * @return {ReactElement} The rendered link element.
 */
const VidyardLink = ({ vidyardVideoUrl, linkText, linkProps, anchorProps, children }) => {
    const [player, setPlayer] = useState(undefined);
    const [foundPlayer, setFoundPlayer] = useState(0); // Using 0 instead of false for direct comparison in render

    useEffect(() => {
        async function fetchPlayers() {
            const isWindowContext = typeof window !== "undefined";

            if (isWindowContext) {
                const uuid = getVidyardUUID(vidyardVideoUrl);
                // Using the Vidyard API to see if there are any Vidyard videos on the page
                // This returns a single entry array with the player object
                let players = await window?.VidyardV4?.api?.getPlayersByUUID(uuid);

                if (!players || players.length === 0) {
                    // Sometimes because of the way the videos are embedded on the page
                    // we need to manually let the Vidyard API know to render a video
                    // This returns the player object
                    players = await window?.vidyardEmbed?.api.renderPlayer({
                        uuid,
                        container: document.querySelector(".vidyard-container"),
                        type: "lightbox",
                    });
                } else {
                    // If the 1st method worked, we need to assign the player object to the variable
                    players = players[0];
                }

                if (players && players.length > 0) {
                    setPlayer(players);
                    setFoundPlayer(1);
                }
            }
        }

        fetchPlayers();
    }, [vidyardVideoUrl]);

    const showLightbox = (e) => {
        e.preventDefault();
        player && player.showLightbox();
    };

    return (
        <>
            <div className="hidden vidyard-container">
                <img
                    alt="video"
                    style={{ maxWidth: "100%" }}
                    className="vidyard-player-embed"
                    src={getVidyardCoverImageUrl(vidyardVideoUrl)}
                    data-uuid={getVidyardUUID(vidyardVideoUrl)}
                    data-v="4"
                    data-type="lightbox"
                    data-autoplay="1"
                />
            </div>
            {foundPlayer > 0 ? (
                <button
                    href="#"
                    {...linkProps}
                    onClick={showLightbox}
                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(linkText) }}
                ></button>
            ) : children ? (
                <a data-found-player={foundPlayer} {...anchorProps}>
                    {children}
                </a>
            ) : (
                <a {...anchorProps} dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(linkText) }}></a>
            )}
        </>
    );
};

export default VidyardLink;
